import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { store } from "../ReduxStore/Store";
import {
  selectFormData,
  setActiveStep,
  setFiles,
  setFormData,
} from "../ReduxStore/UserSlice";
import DraftApplicationService from "../services/DraftApplicationService";
import { toast } from "react-toastify";

const TEMP_LOGOUT_TOKEN = "temp_logout_token";

/**
 * @param {boolean} isApplyLoanPage
 * @returns {Object}
 */
const useFormProtection = (isApplyLoanPage = false) => {
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState(null);
  const [actionType, setActionType] = useState("navigation"); // 'navigation' or 'logout'
  const formData = useSelector(selectFormData);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Check if we should protect navigation (are we on apply loan page with unsaved data)
  const fromConsultantDashboard = location.state?.fromConsultantDashboard === true || location.state?.source === 'consultant';
  const shouldProtectNavigation =
    isApplyLoanPage && DraftApplicationService.shouldOfferDraftSave(formData) && !fromConsultantDashboard;

  /**
   * @param {string} target
   * @param {string} type
   * @returns {boolean}
   */
  const handleNavigationAttempt = useCallback(
    (target, type = "navigation") => {
      if (shouldProtectNavigation) {
        setNavigationTarget(target);
        setActionType(type);
        setShowDraftDialog(true);
        return true;
      }
      return false;
    },
    [shouldProtectNavigation]
  );

  /**
   * @returns {Promise<boolean>}
   */
  const handleSaveDraft = useCallback(async () => {
    try {
      setSavingDraft(true);

      const storageData = {
        currentToken: localStorage.getItem("jwtToken"),
      userBusinessType: localStorage.getItem("userBusinessType"),
      userEntityType: localStorage.getItem("userEntityType"),
      userInfo: localStorage.getItem("user"),
      userTypeInfo: localStorage.getItem("userTypeInfo")
      };

      if (storageData.currentToken && !localStorage.getItem("jwtToken")) {
        localStorage.setItem("jwtToken", storageData.currentToken);
      }

      await DraftApplicationService.saveDraft();
      setSavingDraft(false);
      setShowDraftDialog(false);

      Object.entries(storageData).forEach(([key, value]) => {
        if (value) {
          const storageKey = key === 'currentToken' ? 'jwtToken' : key;
          localStorage.setItem(storageKey, value);
        }
      });

      try {
        if (storageData.userTypeInfo) {
          const userTypeInfo = JSON.parse(storageData.userTypeInfo);
          localStorage.setItem('userTypeInfo', JSON.stringify(userTypeInfo));
        }
      } catch (error) {
        toast.error("Error", error);
      }

      if (navigationTarget) {
        navigate(navigationTarget);
      }

      if (actionType === "logout") {
        window.dispatchEvent(
          new CustomEvent("draftSaved", { detail: { proceedWithLogout: true } })
        );
      }

      return true;
    } catch (error) {
      toast.error("Error saving draft");
      setSavingDraft(false);

      if (navigationTarget) {
        if (navigationTarget === 'back') {
          navigate(-1);
        } else {
          navigate(navigationTarget);
        }
      }

      if (actionType === "logout") {
        window.dispatchEvent(
          new CustomEvent("draftSaved", { detail: { proceedWithLogout: true } })
        );
      }

      return false;
    }
  }, [navigationTarget, actionType, navigate]);

  const handleCancelSaveDraft = useCallback(() => {
    setShowDraftDialog(false);
    
    const storageData = {
      userTypeInfo: localStorage.getItem("userTypeInfo"),
      userBusinessType: localStorage.getItem("userBusinessType"),
      userEntityType: localStorage.getItem("userEntityType"),
      userInfo: localStorage.getItem("user")
    };

    dispatch(setFormData(null));
    dispatch(setFiles(null));
    dispatch(setActiveStep(0));

    localStorage.removeItem("sessionTimeoutInProgress");
    
    Object.entries(storageData).forEach(([key, value]) => {
      if (value) {
        localStorage.setItem(key, value);
      }
    });

    try {
      if (storageData.userTypeInfo) {
        const userTypeInfo = JSON.parse(storageData.userTypeInfo);
        localStorage.setItem('userTypeInfo', JSON.stringify(userTypeInfo));
      }
    } catch (error) {
      toast.error("Error", error);
    }

    if (navigationTarget) {
      if (navigationTarget === 'back') {
        navigate(-1);
      } else {
        navigate(navigationTarget);
      }
    }

    if (actionType === "logout") {
      window.dispatchEvent(
        new CustomEvent("draftSaved", { detail: { proceedWithLogout: true } })
      );
    }
  }, [navigationTarget, actionType, navigate, dispatch]);

  /**
   * @returns {Promise<boolean>}
   */
  const handleAutoSaveDraft = useCallback(async () => {
    if (shouldProtectNavigation) {
      try {
        const storageData = {
          currentToken: localStorage.getItem("jwtToken"),
          userBusinessType: localStorage.getItem("userBusinessType"),
          userEntityType: localStorage.getItem("userEntityType"),
          userInfo: localStorage.getItem("user"),
          userTypeInfo: localStorage.getItem("userTypeInfo")
        };

        setSavingDraft(true);

        if (!formData.userId) {
          console.error(
            "[FORM-PROTECTION] Missing userId in form data - attempting to find from other sources"
          );

          const localStorageUserId = localStorage.getItem("userId");
          let userObj = null;

          try {
            userObj = JSON.parse(localStorage.getItem("user") || "{}");
          } catch (e) {
            console.error(
              "[FORM-PROTECTION] Failed to parse user object from localStorage:",
              e
            );
          }

          const state = store.getState();
          const reduxUser = state.user?.user || {};
          const patchedFormData = {
            ...formData,
            userId:
              formData.userId ||
              reduxUser.userId ||
              reduxUser.id ||
              reduxUser.ID ||
              userObj?.userId ||
              userObj?.id ||
              userObj?.ID ||
              localStorageUserId,
          };

          const result = await DraftApplicationService.saveDraft(
            patchedFormData
          );

          setSavingDraft(false);

          Object.entries(storageData).forEach(([key, value]) => {
            if (value) {
              const storageKey = key === 'currentToken' ? 'jwtToken' : key;
              localStorage.setItem(storageKey, value);
            }
          });

          try {
            if (storageData.userTypeInfo) {
              const userTypeInfo = JSON.parse(storageData.userTypeInfo);
              localStorage.setItem('userTypeInfo', JSON.stringify(userTypeInfo));
            }
          } catch (error) {
            toast.error("Error", error);
          }

          return result?.statusCode === 200;
        }

        const result = await DraftApplicationService.saveDraft();

        setSavingDraft(false);

        Object.entries(storageData).forEach(([key, value]) => {
          if (value) {
            const storageKey = key === 'currentToken' ? 'jwtToken' : key;
            localStorage.setItem(storageKey, value);
          }
        });

        try {
          if (storageData.userTypeInfo) {
            const userTypeInfo = JSON.parse(storageData.userTypeInfo);
            localStorage.setItem('userTypeInfo', JSON.stringify(userTypeInfo));
          }
        } catch (error) {
          toast.error("Error", error);
        }

        return result?.statusCode === 200;
      } catch (error) {
        console.error("[FORM-PROTECTION] Error auto-saving draft:", error);
        console.error("[FORM-PROTECTION] Error stack:", error.stack);

        setSavingDraft(false);
        return false;
      }
    }
    return false;
  }, [shouldProtectNavigation, formData, isApplyLoanPage]);

  return {
    showDraftDialog,
    savingDraft,
    actionType,
    handleNavigationAttempt,
    handleSaveDraft,
    handleCancelSaveDraft,
    handleAutoSaveDraft,
    setShowDraftDialog,
    shouldProtectNavigation,
    navigationTarget,
  };
};

export default useFormProtection;
